<script lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogTitle,
    DialogDescription,
  } from "$lib/components/ui/dialog";
  import { Button } from "$lib/components/ui/button";

  import PerforationEditor from "./PerforationEditor.svelte";
  import PictureEditor from "./PictureEditor.svelte";
  import Picture from "../Picture.svelte";
  import FaceValueEditor from "./FaceValueEditor.svelte";
  import TitleEditor from "./TitleEditor.svelte";
  import DescriptionEditor from "./DescriptionEditor.svelte";

  import GumTypeSelect from './GumTypeSelect.svelte';
  import ColorEditor from './ColorEditor.svelte';
  import PrintRunEditor from './PrintRunEditor.svelte';
  import { StampInSetModel } from "./models/StampInSetModel.svelte"
  import CatalogIdsEditor from "./CatalogIdsEditor.svelte";
  import ZhihaoEditor from "./ZhihaoEditor.svelte";
  import { t } from "$lib/i18n";
  import { createBindableState, type BindableValue } from './utils.svelte';

  interface Props {
    isOpen: BindableValue<boolean>;
    model:  StampInSetModel;
  }

  let { isOpen = $bindable(false), model = $bindable() }: Props = $props();


  let open =createBindableState(
    () => isOpen,
    (v) => isOpen = v,
    false
  );

</script>

<Dialog bind:open={open.current}>
  <DialogContent class="flex flex-col overflow-hidden max-h-[90dvh]">
    <DialogTitle>{$t('editor.stampInSetTitle')}</DialogTitle>
    <DialogDescription>{$t('editor.stampInSetSubtitle')}</DialogDescription>
    <div class="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
      <TitleEditor isReadonly={model.isReadonly} bind:title={model.title} invalid={!model.isReadonly && model.isTitleInvalid}/>
      <DescriptionEditor isReadonly={model.isReadonly} bind:description={model.description} />
      <FaceValueEditor
        isReadonly={model.isReadonly}
        bind:value={model.face_value_value}
        bind:unit={model.face_value_unit}
        invalidAmount={!model.isReadonly && model.isFaceValueAmountInvalid}
        invalidUnit={!model.isReadonly && model.isFaceValueUnitInvalid}
      />  
{#if model.picture}
      <Picture picture={model.picture}/>
{/if}
{#if !model.isReadonly}
      <PictureEditor
        isReadonly={model.isReadonly}
        hasPicture={!!model.picture}
        handleImageUpload={model.handleImageUpload.bind(model)}
        onPhoneUpload={model.setPictureFromPhone.bind(model)}
      />
{/if}
      <PerforationEditor
        isReadonly={model.isReadonly}
        bind:horizontal={model.perforation_count_horizontal}
        bind:vertical={model.perforation_count_vertical}
        bind:type={model.perforation_type}
        bind:security={model.security_perforation}
      />
      <div class="grid grid-cols-4 items-center gap-4">
        <GumTypeSelect bind:value={model.with_gum} isReadonly={model.isReadonly} containerClass="contents" selectClass="col-span-1" />
        <PrintRunEditor isReadonly={model.isReadonly} bind:printRun={model.print_run} containerClass="contents" inputClass="col-span-1" labelClass="justify-end" />
      </div>
      <ColorEditor bind:value={model.color} isReadonly={model.isReadonly} />
      <div class="flex gap-2">
        <!-- Not bindable: the Zhihao is derived from the set, the stamp only fills its slot -->
        <ZhihaoEditor isReadonly={model.isReadonly} zhihao={model.zhihao} isDerived containerClass="contents" />
        <CatalogIdsEditor isReadonly={model.isReadonly} bind:catalogIds={model.catalog_ids} containerClass="contents" />
      </div>
    </div>

  {#if model.showError}
    <div class="p-4 text-sm text-red-500 bg-red-50 rounded-md">
      {model.error ? $t(model.error) : ''}
    </div>
  {/if}
  {#if !model.isReadonly}
    <form onsubmit={(ev: Event) => { model.onsubmit(() => { open.current = false; }, ev) }}>
      <DialogFooter>
        <Button type="submit" disabled={!model.isValid}>{$t(model.submitLabel)}</Button>
      </DialogFooter>
    </form>
  {/if}
  </DialogContent>
</Dialog>
